import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';

type Step = 'delivery' | 'confirm';
type DeliveryType = 'standard' | 'express';

const emirates = ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

interface CartItem {
  id: number;
  name: string;
  price: number;
  image?: string;
  category: string;
  quantity: number;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('delivery');
  const [deliveryType, setDeliveryType] = useState<DeliveryType>('standard');
  const [placing, setPlacing] = useState(false);
  const [orderItems, setOrderItems] = useState<CartItem[]>([]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '+971 ', emirate: 'Dubai', area: '', building: '', apartment: '', notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem('cart');
      if (raw) {
        const parsed = JSON.parse(raw) as CartItem[];
        setOrderItems(parsed);
      }
    } catch {
      setOrderItems([]);
    }
  }, []);

  const subtotal = orderItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const deliveryFee = deliveryType === 'express' ? 35 : 0;
  const total = subtotal + deliveryFee;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Full name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.phone.trim() || form.phone.length < 12) errs.phone = 'Valid UAE phone required';
    if (!form.area.trim()) errs.area = 'Area is required';
    if (!form.building.trim()) errs.building = 'Building is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (step === 'delivery' && validate()) setStep('confirm');
  };

  const handlePlaceOrder = async () => {
    setPlacing(true);
    try {
      const orderNumber = `AIS-${Date.now().toString().slice(-8)}`;
      const estimatedDelivery = deliveryType === 'express'
        ? new Date(Date.now() + 86400000).toLocaleDateString('en-AE', { weekday: 'long', month: 'long', day: 'numeric' })
        : new Date(Date.now() + 4 * 86400000).toLocaleDateString('en-AE', { weekday: 'long', month: 'long', day: 'numeric' });

      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          order_number: orderNumber,
          status: 'pending',
          subtotal,
          delivery_fee: deliveryFee,
          vat: 0,
          total,
          promo_code: null,
          discount: 0,
          delivery_type: deliveryType,
          payment_method: 'cod',
          full_name: form.name,
          email: form.email,
          phone: form.phone,
          emirate: form.emirate,
          area: form.area,
          building: form.building,
          apartment: form.apartment || null,
          notes: form.notes || null,
          estimated_delivery: estimatedDelivery,
        })
        .select('id')
        .maybeSingle();

      if (orderError) throw orderError;

      if (orderData?.id && orderItems.length > 0) {
        const items = orderItems.map(item => ({
          order_id: orderData.id,
          product_id: item.id,
          product_name: item.name,
          product_image: item.image || null,
          product_category: item.category,
          quantity: item.quantity,
          unit_price: item.price,
          total_price: item.price * item.quantity,
        }));
        await supabase.from('order_items').insert(items);
      }

      localStorage.removeItem('cart');
      navigate(`/order-confirmation?order=${orderNumber}&total=${total}&delivery=${estimatedDelivery}&name=${encodeURIComponent(form.name)}&phone=${encodeURIComponent(form.phone)}`);
    } catch {
      alert('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  const steps: { key: Step; label: string; icon: string }[] = [
    { key: 'delivery', label: 'Delivery', icon: 'ri-truck-line' },
    { key: 'confirm', label: 'Confirm', icon: 'ri-check-double-line' },
  ];
  const stepIndex = steps.findIndex(s => s.key === step);

  if (orderItems.length === 0 && step === 'delivery') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
        <Header />
        <div className="pt-32 pb-20 flex flex-col items-center justify-center px-6">
          <div className="w-32 h-32 flex items-center justify-center rounded-full bg-orange-50 mb-6">
            <i className="ri-shopping-cart-line text-6xl text-orange-400"></i>
          </div>
          <h2 className="text-3xl font-black text-gray-800 mb-3">Your cart is empty</h2>
          <p className="text-gray-500 mb-8 text-center max-w-md">Add some products to your cart before checking out.</p>
          <Link
            to="/shop"
            className="px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-line mr-2"></i>Browse Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50/30 via-white to-pink-50/30">
      <Header />
      <div className="pt-24 pb-4 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-black bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent mb-1">Checkout</h1>
          <p className="text-sm text-gray-500">
            <Link to="/" className="hover:text-orange-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <Link to="/cart" className="hover:text-orange-500 transition-colors">Cart</Link>
            <i className="ri-arrow-right-s-line mx-1"></i>
            <span className="text-gray-800">Checkout</span>
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Step Bar */}
        <div className="flex items-center justify-center gap-0 mb-8">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${i <= stepIndex ? 'bg-gradient-to-r from-orange-500 to-pink-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                <i className={`${s.icon} text-sm`}></i>
                <span className="text-sm font-semibold whitespace-nowrap">{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 transition-all duration-300 ${i < stepIndex ? 'bg-gradient-to-r from-orange-500 to-pink-600' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6">
              {step === 'delivery' && (
                <div>
                  <h2 className="text-xl font-black text-gray-800 mb-6 flex items-center gap-2">
                    <i className="ri-map-pin-line text-orange-500"></i> Delivery Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Full Name *</label>
                      <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Mohammed Al-Rashid"
                        className={`w-full px-4 py-3 rounded-2xl border text-sm focus:outline-none transition-all bg-white text-gray-800 ${errors.name ? 'border-red-400' : 'border-gray-200 focus:border-orange-400'}`} />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Email Address *</label>
                      <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com"
                        className={`w-full px-4 py-3 rounded-2xl border text-sm focus:outline-none transition-all bg-white text-gray-800 ${errors.email ? 'border-red-400' : 'border-gray-200 focus:border-orange-400'}`} />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Phone Number *</label>
                      <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+971 50 000 0000"
                        className={`w-full px-4 py-3 rounded-2xl border text-sm focus:outline-none transition-all bg-white text-gray-800 ${errors.phone ? 'border-red-400' : 'border-gray-200 focus:border-orange-400'}`} />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Emirate *</label>
                      <select value={form.emirate} onChange={e => setForm({ ...form, emirate: e.target.value })}
                        className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800 cursor-pointer">
                        {emirates.map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Area / District *</label>
                      <input type="text" value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} placeholder="e.g. Dubai Marina, JBR"
                        className={`w-full px-4 py-3 rounded-2xl border text-sm focus:outline-none transition-all bg-white text-gray-800 ${errors.area ? 'border-red-400' : 'border-gray-200 focus:border-orange-400'}`} />
                      {errors.area && <p className="text-red-500 text-xs mt-1">{errors.area}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Building / Villa *</label>
                      <input type="text" value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} placeholder="Building name or villa number"
                        className={`w-full px-4 py-3 rounded-2xl border text-sm focus:outline-none transition-all bg-white text-gray-800 ${errors.building ? 'border-red-400' : 'border-gray-200 focus:border-orange-400'}`} />
                      {errors.building && <p className="text-red-500 text-xs mt-1">{errors.building}</p>}
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Apartment / Floor</label>
                      <input type="text" value={form.apartment} onChange={e => setForm({ ...form, apartment: e.target.value })} placeholder="Apt 12, Floor 3"
                        className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Delivery Notes</label>
                      <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any special instructions..." rows={3}
                        className="w-full px-4 py-3 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 bg-white text-gray-800 resize-none" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="text-base font-bold text-gray-800 mb-3">Delivery Method</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(['standard', 'express'] as DeliveryType[]).map(type => (
                        <button key={type} onClick={() => setDeliveryType(type)}
                          className={`p-4 rounded-2xl border-2 text-left transition-all duration-300 cursor-pointer ${deliveryType === type ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deliveryType === type ? 'border-orange-500' : 'border-gray-300'}`}>
                              {deliveryType === type && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                            </div>
                            <div>
                              <p className="font-bold text-gray-800 text-sm">{type === 'standard' ? 'Standard Delivery' : 'Express Delivery'}</p>
                              <p className="text-xs text-gray-500">{type === 'standard' ? '3-5 business days' : 'Next day delivery'}</p>
                            </div>
                            <span className={`ml-auto font-bold text-sm ${type === 'standard' ? 'text-green-600' : 'text-orange-600'}`}>
                              {type === 'standard' ? 'FREE' : 'AED 35'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Payment info — COD only, shown inline */}
                  <div className="mt-6 p-4 rounded-2xl border-2 border-green-200 bg-green-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-100 flex-shrink-0">
                        <i className="ri-money-dollar-circle-line text-xl text-green-600"></i>
                      </div>
                      <div>
                        <p className="font-bold text-gray-800 text-sm">Cash on Delivery</p>
                        <p className="text-xs text-gray-500">Pay when your order arrives — no card needed!</p>
                        <p className="text-xs text-green-600 font-medium mt-0.5">Most popular in UAE</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step === 'confirm' && (
                <div>
                  <h2 className="text-xl font-black text-gray-800 mb-6 flex items-center gap-2">
                    <i className="ri-check-double-line text-orange-500"></i> Review Your Order
                  </h2>
                  <div className="space-y-4">
                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                      <h3 className="font-bold text-gray-700 text-sm mb-3">Delivery Address</h3>
                      <p className="text-gray-800 font-semibold">{form.name}</p>
                      <p className="text-gray-600 text-sm">{form.building}{form.apartment ? `, ${form.apartment}` : ''}, {form.area}</p>
                      <p className="text-gray-600 text-sm">{form.emirate}, UAE</p>
                      <p className="text-gray-600 text-sm">{form.phone}</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                      <h3 className="font-bold text-gray-700 text-sm mb-2">Payment</h3>
                      <p className="text-gray-800 font-semibold">Cash on Delivery</p>
                    </div>
                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                      <h3 className="font-bold text-gray-700 text-sm mb-2">Delivery</h3>
                      <p className="text-gray-800 font-semibold">{deliveryType === 'standard' ? 'Standard (3-5 days) — FREE' : 'Express (Next day) — AED 35'}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-8 w-full">
                {step !== 'delivery' && (
                  <button onClick={() => setStep('delivery')}
                    className="flex-shrink-0 px-5 py-3 rounded-full border-2 border-gray-200 text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-500 transition-all cursor-pointer whitespace-nowrap">
                    ← Back
                  </button>
                )}
                {step !== 'confirm' ? (
                  <button onClick={handleNext}
                    className="flex-1 py-3 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-[1.02] transition-all cursor-pointer min-w-0 overflow-hidden">
                    Continue <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                ) : (
                  <button onClick={handlePlaceOrder} disabled={placing}
                    className="flex-1 py-3 rounded-full bg-gradient-to-r from-orange-500 to-pink-600 text-white font-bold hover:scale-[1.02] transition-all cursor-pointer min-w-0 overflow-hidden disabled:opacity-70">
                    {placing ? (
                      <><i className="ri-loader-4-line animate-spin mr-2"></i>Placing Order...</>
                    ) : (
                      <>
                        <i className="ri-check-line mr-2"></i>
                        <span className="hidden sm:inline">Place Order — AED {total}</span>
                        <span className="sm:hidden">Place Order</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100/50 p-6 sticky top-24">
              <h2 className="text-lg font-black text-gray-800 mb-4">Order Summary</h2>
              <div className="space-y-3 mb-4">
                {orderItems.map(item => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={item.image || ''} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 line-clamp-2">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      <p className="text-sm font-bold text-gray-800">AED {item.price * item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Subtotal</span><span className="font-semibold">AED {subtotal}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Delivery</span>
                  <span className={`font-semibold ${deliveryFee === 0 ? 'text-green-600' : 'text-gray-800'}`}>{deliveryFee === 0 ? 'FREE' : `AED ${deliveryFee}`}</span>
                </div>
                <div className="flex justify-between font-black text-gray-800 pt-2 border-t border-gray-100">
                  <span>Total</span>
                  <span className="text-lg bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">AED {total}</span>
                </div>
              </div>
              <div className="mt-4 p-3 rounded-xl bg-green-50 border border-green-100">
                <p className="text-xs text-green-700 font-medium flex items-center gap-1">
                  <i className="ri-shield-check-line"></i> Secure checkout — Your data is protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
